import { useCallback } from 'react';

import { useMutation } from '@tanstack/react-query';

import { useToastStore } from '@pkg/components';
import { useUserAddresses } from '@pkg/hooks/queryHooks/usePartnerIntegrations.ts';
import { type RegisterUser } from '@pkg/types';

import { callRegisterEmptyUser } from '@app/client/resources/users.ts';
import { useAuthTokens } from '@app/store/zustand';
import { usePartnerWalletStore } from '@app/store/zustand/partner-integration/usePartnerWalletStore.ts';

export function useCreateDraftUser() {
    const setTokens = useAuthTokens((s) => s.setAuthTokens);
    const setDraftUser = useAuthTokens((s) => s.setDraftUser);
    const isDraftUser = useAuthTokens((s) => s.isDraftUser);
    const { addToast } = useToastStore();
    const storeAddresses = useUserAddresses();

    const userAddresses = usePartnerWalletStore((s) => s.userAddresses);
    const partnerConversionId = usePartnerWalletStore((s) => s.partnerConversionId);

    const { mutateAsync: register } = useMutation({
        mutationFn: (value: RegisterUser) => callRegisterEmptyUser({ value }),
    });
    return useCallback(async () => {
        if (isDraftUser) {
            return;
        }
        const r = await register({ partner_conversion_id: partnerConversionId });

        if (r.ok && r.value) {
            setTokens({ tokens: r.value });
            setDraftUser(true);
            if (userAddresses) {
                void storeAddresses(userAddresses);
            }
            return;
        } else {
            if (!r.ok && r.error) addToast({ message: r.error, minimal: true });
        }
    }, [isDraftUser, addToast, partnerConversionId, register, setDraftUser, setTokens, storeAddresses, userAddresses]);
}
